import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setDataLayout } from "../../data/redux/themeSettingSlice";
import ImageWithBasePath from "../imageWithBasePath";
import {
  setMobileSidebar,
  toggleMiniSidebar,
} from "../../data/redux/sidebarSlice";
import { all_routes } from "../../../feature-module/router/all_routes";
import { useAuth } from "../../../hooks/authContext";
import {
  getAPIHeaders,
  removeAccessToken,
  removeAuthToken,
  removeRefreshAuthToken,
  removeTenantID,
  getRememberMe
} from "../../../common/Utils";
import { useAccess } from "../../../hooks/accessContext";
import useFetch from "../../../hooks/useFetch";
import { API } from "../../../common/Constants";
import { Profile, Notification } from "../../../types/dataModel";
import PreLoader from "../preLoader";
import moment from "moment";
import { Empty } from "antd";
import BulkUploadModal from "../../../feature-module/pages/bulk-upload/BulkUploadModal";
import HeaderLogo from "../headerLogo";
import TermsAndConditions from "../termsAndConditions";

const DEFAULT_PROFILE_PIC = "/assets/img/profiles/avatar-27.jpg";

const Header = () => {
  const { hasBulkUploadMgmtCreate } = useAccess();
  const { callFetch } = useFetch();
  const routes = all_routes;
  const dispatch = useDispatch();
  const dataLayout = useSelector((state: any) => state.themeSetting.dataLayout);
  const Location = useLocation();

  const [subOpen, setSubopen] = useState<any>("");
  const [subsidebar, setSubsidebar] = useState("");

  const { setAuthUser, setIsLoggedIn } = useAuth();
  const { setUserAccess } = useAccess();

  const [userProfile, setUserProfile] = useState<Profile>();
  const [imagePreview, setImagePreview] = useState(DEFAULT_PROFILE_PIC);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const [isInProgress, setIsInProgress] = useState(false);

  const navigate = useNavigate();
  const notificationInterval = useRef<NodeJS.Timeout | null>(null);
  const [showMobileNotifications, setShowMobileNotifications] = useState(false);

  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);

  const toggleSidebar = (title: any) => {
    localStorage.setItem("menuOpened", title);
    if (title === subOpen) {
      setSubopen("");
    } else {
      setSubopen(title);
    }
  };

  const toggleSubsidebar = (subitem: any) => {
    if (subitem === subsidebar) {
      setSubsidebar("");
    } else {
      setSubsidebar(subitem);
    }
  };
  const mobileSidebar = useSelector(
    (state: any) => state.sidebarSlice.mobileSidebar
  );

  const toggleMobileSidebar = () => {
    dispatch(setMobileSidebar(!mobileSidebar));
  };

  const handleToggleMiniSidebar = () => {
    if (dataLayout === "mini_layout") {
      dispatch(setDataLayout("default_layout"));
      localStorage.setItem("dataLayout", "default_layout");
    } else {
      dispatch(toggleMiniSidebar());
    }
  };

  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((err) => {});
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch((err) => {});
        }
        setIsFullscreen(false);
      }
    }
  };

  const toggleMobileNotifications = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowMobileNotifications(!showMobileNotifications);
  };

  const handleOpenBulkUpload = () => {
    if (hasBulkUploadMgmtCreate() === true) {
      setShowBulkUploadModal(true);
    }
  };

  const handleCloseBulkUpload = () => {
    setShowBulkUploadModal(false);
  };

  const onClickLogOut = () => {
    !getRememberMe() && removeTenantID();
    removeAuthToken();
    removeRefreshAuthToken();
    removeAccessToken();
    setIsLoggedIn(false);
    setAuthUser(null);
    setUserAccess([]);
    navigate("/login");
  };

  useEffect(() => {
    fetchProfile();
    fetchProfilePic();
    fetchNotification();
    checkTermsAndConditions();

    notificationInterval.current = setInterval(() => {
      fetchNotification();
    }, 30000);

    return () => {
      if (notificationInterval.current) {
        clearInterval(notificationInterval.current);
      }
    };
  }, []);

  const checkTermsAndConditions = () => {
      callFetch(API.TERMS_AND_CONDITIONS_STATUS, "GET", getAPIHeaders(), null, null)
        .then((response) => {
          setShowTermsAndConditions(!response?.hasConsented);
        }).catch((error) => {
          console.error("Error fetching terms and conditions status:", error);
        });
    };

  const fetchProfile = () => {
    callFetch(`${API.PROFILE}`, "GET", getAPIHeaders(), null, null)
      .then((res) => {
        setUserProfile(res);
      })
      .catch((err) => {});
  };

  const fetchNotification = () => {
    callFetch(`${API.NOTIFICATION_UNREAD}`, "GET", getAPIHeaders(), null, null)
      .then((res) => {
        if (res && res.length) {
            const sortedNotifications = [...res].sort((a, b) => 
            new Date(b.sentAt || 0).getTime() - new Date(a.sentAt || 0).getTime());
            setNotifications(sortedNotifications);
        } else {
          setNotifications([]);
        }
      })
      .catch((err) => {});
  };

  const fetchProfilePic = () => {
    callFetch(`${API.PROFILE_IMAGE}`, "GET", getAPIHeaders(), null, null)
      .then((res) => {
        if (res && res.fileUrl) {
          setImagePreview(res.fileUrl);
        } else {
          setImagePreview(DEFAULT_PROFILE_PIC);
        }
      })
      .catch(() => {
        setImagePreview(DEFAULT_PROFILE_PIC);
      });
  };

  const ackNotification = (ids?: number[]) => {
    setIsInProgress(true);
    callFetch(`${API.NOTIFICATION_ACK}`, "POST", getAPIHeaders(), null, ids)
      .then(() => {
        setIsInProgress(false);
        fetchNotification();
      })
      .catch((err) => {
        setIsInProgress(false);
      });
  };

  return (
    <>
      <PreLoader showLoader={isInProgress} />
      <TermsAndConditions 
        show={showTermsAndConditions} 
        onAccept={() => {setShowTermsAndConditions(false)}} 
        onDecline={onClickLogOut}/>
      {/* Header */}
      <div className="header">
        <div className="main-header">
          <div className="header-left">
            <Link to="/index" className="d-flex justify-content-center">
              <HeaderLogo />
            </Link>
          </div>
          <Link
            id="mobile_btn"
            onClick={toggleMobileSidebar}
            className="mobile_btn"
            to="#sidebar"
          >
            <span className="bar-icon">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </Link>
          <div className="header-user">
            <div className="nav user-menu nav-list">
              <div
                className="me-auto d-flex align-items-center"
                id="header-search"
              >
                <Link
                  id="toggle_btn"
                  to="#"
                  onClick={handleToggleMiniSidebar}
                  className="btn btn-menubar me-1"
                >
                  <i className="ti ti-arrow-bar-to-left"></i>
                </Link>
              </div>

              <div className="d-flex align-items-center">
                <div className="me-1">
                  <Link
                    to="#"
                    onClick={toggleFullscreen}
                    className="btn btn-menubar btnFullscreen"
                    title="Toggle Fullscreen"
                  >
                    <i
                      className="ti ti-maximize"
                    ></i>
                  </Link>
                </div>
                {/* Bulk Upload Button */}
                {hasBulkUploadMgmtCreate() && (
                  <div className="me-1">
                    <Link
                      to="#"
                      onClick={handleOpenBulkUpload}
                      className="btn btn-menubar position-relative me-1"
                      title="Bulk Upload"
                    >
                      <i
                        className="ti ti-upload"
                      >
                      <span
                        className="position-absolute top-1 start-100 translate-middle text-primary"
                      >
                        <i
                          className="ti ti-file-spreadsheet"
                          style={{ fontSize: "12px" }}
                        ></i>
                      </span>
                      </i>
                    </Link>
                  </div>
                )}
                <div className="me-1 notification_item">
                  <Link
                    to="#"
                    className="btn btn-menubar position-relative me-1"
                    id="notification_popup"
                    data-bs-toggle="dropdown"
                    title="Notifications"
                  >
                    <i
                      className="ti ti-bell"
                    ></i>
                    {notifications?.length ? (
                      <span className="notification-status-dot"></span>
                    ) : (
                      <></>
                    )}
                  </Link>
                  <div className="dropdown-menu dropdown-menu-end notification-dropdown p-4">
                    <div className="d-flex align-items-center justify-content-between border-bottom p-0 pb-3 mb-3">
                      <h4 className="notification-title">
                        Notifications ({notifications?.length})
                      </h4>
                      <div className="d-flex align-items-center">
                        <button
                          className="btn text-primary fs-15 me-3 lh-1"
                          onClick={(event: any) => {
                            event.stopPropagation();
                            event.preventDefault();
                            let ids = notifications
                              ?.map(
                                (notification: Notification) => notification?.id
                              )
                              .filter((id): id is number => id !== undefined);
                            if (ids?.length) {
                              ackNotification(ids);
                            }
                          }}
                        >
                          Mark all as read
                        </button>
                      </div>
                    </div>
                    <div className="noti-content d-flex" style={{ maxHeight: "300px", overflowY: "auto", width: "100%" }}>
                      <div className="d-flex flex-column w-100">
                        {notifications?.length ? (
                          notifications?.map(
                            (notification: Notification, index: number) => {
                              return (
                                <div className="border-bottom mb-3 pb-3">
                                  <div className="pb-0">
                                    <div className="d-flex">
                                      <div className="flex-grow-1">
                                        <p className="mb-2">
                                          <span className="text-dark fw-semibold">
                                            {notification?.title}{" "}
                                          </span>
                                          <span className="text-gray">
                                            {" "}
                                            {notification?.message}
                                          </span>
                                        </p>
                                        <span className="text-dark">
                                          {formatNotificationTime(
                                            notification?.sentAt
                                          )}
                                        </span>
                                        <div className="d-flex justify-content-start align-items-center mt-2">
                                          <button
                                            className="btn btn-primary btn-sm me-2"
                                            onClick={(event: any) => {
                                              event.stopPropagation();
                                              event.preventDefault();
                                              if (notification?.id) {
                                                ackNotification([
                                                  notification?.id,
                                                ]);
                                              }
                                            }}
                                          >
                                            Mark as Read
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                          )
                        ) : (
                          <div className="d-flex justify-content-center">
                            <Empty />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="dropdown profile-dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle d-flex align-items-center"
                    data-bs-toggle="dropdown"
                    title="Profile"
                  >
                    <span className="avatar avatar-sm online">
                      <ImageWithBasePath
                        src={DEFAULT_PROFILE_PIC}
                        url={imagePreview}
                        alt="Img"
                        className="img-fluid rounded-circle"
                      />
                    </span>
                  </Link>
                  <div className="dropdown-menu shadow-none">
                    <div className="card mb-0">
                      <div className="card-header">
                        <div className="d-flex align-items-center">
                          <span className="avatar avatar-lg me-2 avatar-rounded">
                            <ImageWithBasePath
                              src={DEFAULT_PROFILE_PIC}
                              url={imagePreview}
                              alt="img"
                            />
                          </span>
                          <div>
                            <h5 className="mb-0">
                              {userProfile?.firstName} {userProfile?.lastName}
                            </h5>
                            <p className="fs-12 fw-medium mb-0">
                              {userProfile?.email}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="card-body">
                        <Link
                          className="dropdown-item d-inline-flex align-items-center p-0 py-2"
                          to={routes.profile}
                        >
                          <i className="ti ti-user-circle me-1"></i>My Profile
                        </Link>
                      </div>
                      <div className="card-footer">
                        <Link
                          className="dropdown-item d-inline-flex align-items-center p-0 py-2"
                          to=""
                          onClick={onClickLogOut}
                        >
                          <i className="ti ti-login me-2"></i>Logout
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="dropdown mobile-user-menu">
            <Link
              to="#"
              className="nav-link dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="fa fa-ellipsis-v"></i>
            </Link>
            <div className="dropdown-menu dropdown-menu-end">
              <Link className="dropdown-item" to={routes.profile}>
                <i className="ti ti-user-circle me-2"></i>My Profile
              </Link>
              {hasBulkUploadMgmtCreate() && (
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={handleOpenBulkUpload}
                >
                  <i className="ti ti-upload me-2"></i>Bulk Upload
                </Link>
              )}
              <Link
                className="dropdown-item d-flex align-items-center"
                to="#"
                onClick={toggleMobileNotifications}
              >
                <i className="ti ti-bell me-2"></i>
                Notifications
                {notifications?.length > 0 && (
                  <span className="badge bg-primary rounded-pill ms-2">
                    {notifications.length}
                  </span>
                )}
              </Link>
              <Link className="dropdown-item" to="#" onClick={toggleFullscreen}>
                <i className="ti ti-maximize me-2"></i>Toggle Fullscreen
              </Link>
              <Link className="dropdown-item" to="" onClick={onClickLogOut}>
                <i className="ti ti-login me-2"></i>Logout
              </Link>
            </div>
          </div>

          {/* Mobile Notifications Modal */}
          {showMobileNotifications && (
            <div
              className="modal fade show d-block"
              style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      Notifications ({notifications?.length})
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowMobileNotifications(false)}
                    ></button>
                  </div>
                  <div
                    className="modal-body"
                    style={{ maxHeight: "70vh", overflowY: "auto" }}
                  >
                    {notifications?.length ? (
                      notifications?.map(
                        (notification: Notification, index: number) => (
                          <div key={index} className="border-bottom mb-3 pb-3">
                            <div className="pb-0">
                              <div className="d-flex">
                                <div className="flex-grow-1">
                                  <p className="mb-2">
                                    <span className="text-dark fw-semibold">
                                      {notification?.title}{" "}
                                    </span>
                                    <span className="text-gray">
                                      {" "}
                                      {notification?.message}
                                    </span>
                                  </p>
                                  <span className="text-dark">
                                    {formatNotificationTime(
                                      notification?.sentAt
                                    )}
                                  </span>
                                  <div className="d-flex justify-content-start align-items-center mt-2">
                                    <button
                                      className="btn btn-primary btn-sm me-2"
                                      onClick={() => {
                                        if (notification?.id) {
                                          ackNotification([notification?.id]);
                                        }
                                      }}
                                    >
                                      Mark as Read
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      )
                    ) : (
                      <div className="d-flex justify-content-center">
                        <Empty />
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    {notifications?.length > 0 && (
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          let ids = notifications
                            ?.map(
                              (notification: Notification) => notification?.id
                            )
                            .filter((id): id is number => id !== undefined);
                          if (ids?.length) {
                            ackNotification(ids);
                          }
                          setShowMobileNotifications(false);
                        }}
                      >
                        Mark All as Read
                      </button>
                    )}
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowMobileNotifications(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* /Header */}
      {/* Bulk Upload Modal */}
      <BulkUploadModal
        show={showBulkUploadModal}
        onHide={handleCloseBulkUpload}
      />
    </>
  );
};

function formatNotificationTime(timestamp?: string) {
  const now = moment();
  const time = moment(timestamp);
  const diffInHours = now.diff(time, "hours");

  if (diffInHours < 24) {
    return time.fromNow();
  } else {
    return time.format("MMMM Do YYYY, h:mm A");
  }
}

export default Header;
